//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';
import Logo from '../../icons/loop_logo.png';
import Illustration from '../../icons/logout_confirmation_illustration.png';

interface LogoutConfirmationProps {
  onLogout: () => void;
  onCancel: () => void;
}

const LogoutConfirmation: React.FC<LogoutConfirmationProps> = ({
  onLogout,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <div
      id="id-logout-confirm-container"
      data-testid="logout-confirm-container"
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-logout-bg dark:bg-logout-bg-dark"
    >
      {/* Figma: 3323:3408 — pixel-exact card dimensions */}
      {/* Confirmation card */}
      <div
        id="id-logout-confirm-card"
        data-testid="logout-confirm-card"
        className="relative z-10 flex h-[616px] w-[638px] flex-col items-center rounded-tl border border-logout-border bg-logout-card dark:border-logout-border-dark dark:bg-logout-card-dark"
      >
        {/* Logo */}
        <img
          src={Logo}
          alt="DevOps Loop Logo"
          className="mt-[113px] h-[216px] w-[461px] object-contain"
        />

        {/* LOGOUT title */}
        <p className="mt-[47px] text-center text-xl font-bold leading-[21px] text-logout-text dark:text-logout-text-dark">
          {t('LogoutTitle', 'LOGOUT')}
        </p>

        {/* Confirmation message */}
        <p className="mt-1.5 max-w-[448px] text-center text-lg font-medium leading-[21px] text-logout-text dark:text-logout-text-dark">
          {t('LogoutConfirmation', 'Are you sure you want to logout?')}
        </p>

        {/* Buttons */}
        <div data-testid="logout-confirm-buttons" className="mt-8 flex gap-4">
          <button
            id="cancel-logout-button"
            className="h-10 w-[118px] rounded border border-logout-cancel bg-logout-card text-sm font-medium tracking-[0.16px] text-logout-cancel transition-colors hover:bg-blue-50 dark:border-logout-cancel-dark dark:bg-logout-card-dark dark:text-logout-cancel-dark dark:hover:bg-blue-950"
            onClick={onCancel}
          >
            {t('Cancel')}
          </button>
          <button
            id="confirm-logout-button"
            className="h-10 w-[110px] rounded bg-logout-confirm text-sm font-medium text-white transition-colors hover:bg-logout-confirm-hover dark:bg-logout-confirm-dark dark:hover:bg-logout-confirm-dark-hover"
            onClick={onLogout}
          >
            {t('Logout')}
          </button>
        </div>
      </div>

      {/* Bottom-right illustration */}
      <img
        src={Illustration}
        alt="Logout illustration"
        className="pointer-events-none absolute bottom-0 right-0 w-[648px] select-none"
      />
    </div>
  );
};

export default LogoutConfirmation;
